import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, count, personLabel } from "@/lib/format";
import { useDebounced } from "./useDebounced";

type Filter = "all" | "internal" | "deactivated";

export function UsersScreen() {
  const api = useApi();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const search = useDebounced(q, 250);

  const users = useQuery({
    queryKey: ["admin", "users", { search, filter }],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/users", {
        params: { query: { q: search || undefined, filter, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="People"
        title="People"
        description="Deactivated accounts are hidden by default — they are invisible in the product and waiting on the purge job."
      />
      <ListScaffold
        filters={
          <>
            <Input
              placeholder="Search email, name or handle"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-[260px]"
            />
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
            >
              <option value="all">Everyone</option>
              <option value="internal">Internal only</option>
              <option value="deactivated">Deactivated</option>
            </Select>
          </>
        }
        isLoading={users.isLoading}
        isError={users.isError}
        onRetry={() => void users.refetch()}
        isEmpty={(users.data?.items.length ?? 0) === 0}
        emptyTitle="Nobody matches that"
        emptyHint="Try a shorter search, or a different filter."
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Person</TH>
                <TH>Email</TH>
                <TH align="right">Reviews</TH>
                <TH align="right">Check-ins</TH>
                <TH align="right">Photos</TH>
                <TH>Joined</TH>
                <TH>State</TH>
              </TR>
            </THead>
            <tbody>
              {users.data?.items.map((user) => (
                <TR key={user.id} onClick={() => navigate(`/users/${user.id}`)}>
                  <TD>
                    <span className="font-medium text-ink">
                      {personLabel(user)}
                    </span>
                    {user.displayName && user.handle ? (
                      <span className="text-muted"> · {user.displayName}</span>
                    ) : null}
                  </TD>
                  <TD mono muted>
                    {user.email}
                  </TD>
                  <TD align="right" mono>
                    {count(user.reviewCount)}
                  </TD>
                  <TD align="right" mono>
                    {count(user.checkInCount)}
                  </TD>
                  <TD align="right" mono>
                    {count(user.photoCount)}
                  </TD>
                  <TD muted>{ago(user.createdAt)}</TD>
                  <TD>
                    <span className="flex gap-1">
                      {user.isInternal ? (
                        <Badge tone="warn">internal</Badge>
                      ) : null}
                      {user.deletedAt ? (
                        <Badge tone="danger">deactivated</Badge>
                      ) : null}
                    </span>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
